/**
 * Main/backup redundancy on the wire (PLAN §8, D9): a production's
 * redundancy settings and the switchover answers. The routes
 * (`redundancyRoutes.ts`) are the NODE's: a switchover moves the active role
 * between the two nodes of a site, which only they can do; the cloud does not
 * serve them (its site link will carry them later).
 */
import { ulidSchema } from '@acp/domain/primitives.js';
import { openEnum } from './openEnum.js';
import { z } from 'zod';
import { boundedIdWireSchema } from './primitives.js';

/** A production's redundancy settings, replicated between the pair. */
export const productionSettingsWireSchema = z
  .object({
    productionId: ulidSchema,
    /** D9's per-production "manual only": never fail over automatically (switchover by hand still works). */
    manualOnly: z.boolean(),
    /** When it was last written (epoch ms): the pair merges the settings last-writer-wins by it. */
    updatedAt: z.number().int().nonnegative(),
    updatedBy: z.string().min(1).max(128),
  })
  .strict();
export type ProductionSettingsWire = z.infer<typeof productionSettingsWireSchema>;

export const updateRedundancyRequestWireSchema = z.object({ manualOnly: z.boolean() }).strict();
export const redundancySettingsResponseWireSchema = z.object({ settings: productionSettingsWireSchema }).strict();

/** One device of a promotion (D8's seeding order). */
export const seededDeviceWireSchema = z
  .object({
    deviceId: z.string().min(1).max(128),
    /** Where its state came from: read from the device, the old active's replicated actual state, or a resync. */
    seededFrom: openEnum(['device', 'replicated', 'resync']),
    latencyMs: z.number().nonnegative(),
    /** One-shot layers (a playing clip) the resync left alone: re-firing them needs an operator. */
    oneShotPending: z.array(z.string().max(128)),
  })
  .strict();

export const switchoverResultWireSchema = z
  .object({
    productionId: ulidSchema,
    /** The node that was active. */
    from: boundedIdWireSchema,
    /** The node that is active now. */
    to: boundedIdWireSchema,
    /** The new term (D9: every promotion bumps it). */
    term: z.number().int().nonnegative(),
    /** From the request to the new active driving the devices (PLAN §1.1: < 500 ms). */
    durationMs: z.number().nonnegative(),
    /** The old active's demotion (mute, handover). */
    demoteMs: z.number().nonnegative(),
    /** The new active's promotion (device seeding). */
    promoteMs: z.number().nonnegative(),
    /** Commands the old active had in flight when it was muted: they may or may not have executed (D8). */
    uncertainCommands: z.number().int().nonnegative(),
    devices: z.array(seededDeviceWireSchema),
    /** Devices that did not connect and seed in time (disconnected-policy devices). */
    pendingDevices: z.array(z.string().max(128)),
  })
  .strict();
export type SwitchoverResultWire = z.infer<typeof switchoverResultWireSchema>;

export const switchoverRequestWireSchema = z.object({}).strict();

export const switchoverAllResponseWireSchema = z
  .object({
    /** This node: whose active productions were moved. */
    from: boundedIdWireSchema,
    switched: z.array(switchoverResultWireSchema),
    /** Productions that could not be moved, and why (no ready standby, the peer unreachable…). */
    failed: z.array(z.object({ productionId: ulidSchema, reason: z.string().max(1000) }).strict()),
  })
  .strict();
export type SwitchoverAllResponseWire = z.infer<typeof switchoverAllResponseWireSchema>;
